import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { proveedoresService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/ui/Toast';
import Dialogo from '../../components/ui/Dialogo';
import { btnPrimario, btnSecundario, chip } from '../../components/ui/estilos';
import { Campo, dosColumnas, entrada, tarjetaLista } from '../agenda/campos';

const VACIO = { nombre: '', categoria: '', contacto: '', telefono: '', email: '', notas: '', activo: true };
type Form = typeof VACIO;
const deProveedor = (p: any): Form => ({ nombre: p.nombre || '', categoria: p.categoria || '', contacto: p.contacto || '', telefono: p.telefono || '', email: p.email || '', notas: p.notas || '', activo: p.activo !== false });

export default function Proveedores() {
  const { usuario } = useAuth();
  const [toast, say] = useToast();
  const [lista, setLista] = useState<any[]>([]);
  const [busca, setBusca] = useState('');
  const [selId, setSelId] = useState<string | null>(null);
  const [nuevo, setNuevo] = useState(false);

  const cargar = useCallback(() => proveedoresService.getAll().then(r => setLista(r.data)).catch(() => say('No se pudieron cargar los proveedores.')), [say]);
  useEffect(() => { cargar(); }, [cargar]);

  const q = busca.trim().toLowerCase();
  const visibles = lista.filter(p => !q || [p.nombre, p.categoria, p.contacto].some(v => (v || '').toLowerCase().includes(q)));
  const sel = lista.find(p => p.id === selId) || null;

  return (
    <div className="paneles" style={{ margin: '-22px -24px', height: 'calc(100% + 44px)', minHeight: 480, display: 'grid', gridTemplateColumns: 'minmax(260px,1fr) clamp(320px,40%,480px)' }}>
      <section aria-label="Proveedores" style={{ minHeight: 0, overflowY: 'auto', padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Link to="/requisiciones" style={{ fontSize: 14, fontWeight: 700, color: 'var(--muted)', textDecoration: 'none' }}>← Requisiciones</Link>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar proveedor, categoría o contacto…" aria-label="Buscar proveedor" style={{ ...entrada, flex: 1, minWidth: 200, width: 'auto', borderRadius: 12 }} />
          <button onClick={() => setNuevo(true)} style={{ ...btnPrimario, height: 48, fontSize: 15 }}>+ Nuevo proveedor</button>
        </div>
        {!visibles.length && <div style={{ padding: 30, textAlign: 'center', color: 'var(--muted)' }}>{lista.length ? 'No hay proveedores con ese filtro.' : 'Aún no hay proveedores.'}</div>}
        {visibles.map(p => (
          <button key={p.id} onClick={() => setSelId(p.id)} aria-pressed={selId === p.id} style={{ ...tarjetaLista(selId === p.id), opacity: p.activo ? 1 : .6 }}>
            <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <b style={{ fontSize: 16 }}>{p.nombre}</b>
              {!p.activo && <span style={{ ...chip('var(--well)', 'var(--muted)'), fontSize: 12 }}>Inactivo</span>}
            </span>
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>{[p.categoria, p.contacto, p.num_insumos ? `${p.num_insumos} insumos` : ''].filter(Boolean).join(' · ') || 'Sin datos'}</span>
          </button>
        ))}
      </section>
      <aside aria-label="Detalle del proveedor" style={{ borderLeft: '1px solid var(--line)', background: 'var(--surface)', minHeight: 0, overflowY: 'auto' }}>
        {sel
          ? <EditorProveedor key={sel.id} proveedor={sel} puedeBorrar={usuario?.rol === 'admin'} say={say} onCambio={cargar} onEliminado={() => { setSelId(null); cargar(); }} />
          : <div style={{ height: '100%', display: 'grid', placeItems: 'center', padding: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 16 }}>Elige un proveedor para ver su detalle.</div>}
      </aside>
      {nuevo && <NuevoProveedor say={say} onCerrar={() => setNuevo(false)} onCreado={id => { setNuevo(false); setSelId(id); cargar(); }} />}
      {toast}
    </div>
  );
}

function CamposProveedor({ f, set }: { f: Form; set: (k: keyof Form, v: any) => void }) {
  return <>
    <Campo label="Nombre"><input value={f.nombre} onChange={e => set('nombre', e.target.value)} maxLength={150} style={entrada} /></Campo>
    <div style={dosColumnas}>
      <Campo label="Categoría"><input value={f.categoria} onChange={e => set('categoria', e.target.value)} placeholder="Ej. Verduras" maxLength={80} style={entrada} /></Campo>
      <Campo label="Contacto"><input value={f.contacto} onChange={e => set('contacto', e.target.value)} maxLength={120} style={entrada} /></Campo>
    </div>
    <div style={dosColumnas}>
      <Campo label="Teléfono"><input type="tel" value={f.telefono} onChange={e => set('telefono', e.target.value)} maxLength={40} style={entrada} /></Campo>
      <Campo label="Correo"><input type="email" value={f.email} onChange={e => set('email', e.target.value)} maxLength={120} style={entrada} /></Campo>
    </div>
    <Campo label="Notas"><textarea value={f.notas} onChange={e => set('notas', e.target.value)} rows={3} style={{ ...entrada, height: 'auto', padding: '10px 14px', resize: 'vertical' }} /></Campo>
  </>;
}

function EditorProveedor({ proveedor, puedeBorrar, say, onCambio, onEliminado }: { proveedor: any; puedeBorrar: boolean; say: (m: string) => void; onCambio: () => void; onEliminado: () => void }) {
  const [f, setF] = useState(deProveedor(proveedor));
  const set = (k: keyof Form, v: any) => setF(x => ({ ...x, [k]: v }));
  const cambio = JSON.stringify(f) !== JSON.stringify(deProveedor(proveedor));

  const guardar = async (datos: Form) => {
    if (!datos.nombre.trim()) { say('El proveedor necesita nombre.'); return; }
    try { await proveedoresService.actualizar(proveedor.id, { ...datos, nombre: datos.nombre.trim() }); say('Proveedor guardado.'); onCambio(); }
    catch (e: any) { say(e.response?.data?.error || 'No se pudo guardar.'); }
  };
  const eliminar = async () => {
    if (!window.confirm(`¿Eliminar el proveedor "${proveedor.nombre}"?`)) return;
    try { await proveedoresService.eliminar(proveedor.id); say(`${proveedor.nombre} eliminado.`); onEliminado(); }
    catch (e: any) { say(e.response?.data?.error || 'No se pudo eliminar.'); }
  };

  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <h2 style={{ margin: 0, fontSize: 22 }}>{proveedor.nombre}</h2>
      <CamposProveedor f={f} set={set} />
      {cambio && <button onClick={() => guardar(f)} style={{ ...btnPrimario, height: 48, fontSize: 15 }}>Guardar cambios</button>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => guardar({ ...deProveedor(proveedor), activo: !proveedor.activo })} style={{ ...btnSecundario, flex: 1, height: 44, fontSize: 14 }}>
          {proveedor.activo ? 'Marcar inactivo' : 'Reactivar'}
        </button>
        {puedeBorrar && <button onClick={eliminar} style={{ ...btnSecundario, height: 44, fontSize: 14, color: 'var(--danger)' }}>Eliminar proveedor</button>}
      </div>
    </div>
  );
}

function NuevoProveedor({ say, onCerrar, onCreado }: { say: (m: string) => void; onCerrar: () => void; onCreado: (id: string) => void }) {
  const [f, setF] = useState(VACIO);
  const [error, setError] = useState('');
  const set = (k: keyof Form, v: any) => setF(x => ({ ...x, [k]: v }));
  const crear = async () => {
    if (!f.nombre.trim()) { setError('Escribe el nombre del proveedor.'); return; }
    try { const { data } = await proveedoresService.crear({ ...f, nombre: f.nombre.trim() }); say(`${f.nombre.trim()} agregado.`); onCreado(data.id); }
    catch (e: any) { setError(e.response?.data?.error || 'No se pudo guardar.'); }
  };
  return (
    <Dialogo titulo="Nuevo proveedor" onCerrar={onCerrar} ancho={480}>
      <CamposProveedor f={f} set={set} />
      {error && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={onCerrar} style={{ ...btnSecundario, flex: 1, height: 52 }}>Cancelar</button>
        <button onClick={crear} style={{ ...btnPrimario, flex: 2, height: 52, fontSize: 16 }}>Guardar proveedor</button>
      </div>
    </Dialogo>
  );
}
